import { getCustomers } from 'services/customers'
import { searchOrganizationsAdmin } from 'services/organizations'
import { searchPeopleAdmin } from 'services/people'
import { searchSurveysAdmin } from 'services/surveys'
import { searchItineraries } from 'services/itineraries'
import { getUsers } from 'services/users'
import debounce from 'utils/debounce'
import { getFullName } from '@the-platform-group/formatters/people'

const transformForSelectDisplayCustomer = ({ id, name, type }) => ({
  id,
  label: name,
  value: id,
  secondaryText: type ? `Account type: ${type}` : '',
})

// Transform organizations for select display
const transformForSelectDisplayOrganization = organization => ({
  id: organization.id,
  label: organization.name,
  value: organization.id,
})

// Transform people for select display
const transformForSelectDisplayPeople = person => ({
  id: person.id,
  label: getFullName(person),
  value: person.id,
})

const transformForSelectDisplaySurvey = survey => ({
  id: survey.id,
  label: survey.name,
  value: survey.id,
})

const transformForSelectDisplayItinerary = itinerary => ({
  id: itinerary.id,
  label: itinerary.name,
  value: itinerary.id,
})

async function loadCustomers(params) {
  const response = await getCustomers(params)
  return response?.data?.map(transformForSelectDisplayCustomer)
}

// Search for organizations
async function loadOrganizations(params) {
  const response = await searchOrganizationsAdmin(params)
  return response?.data?.map(transformForSelectDisplayOrganization)
}

// Search for people
async function loadPeople(params) {
  const response = await searchPeopleAdmin(params)
  return response?.data?.map(transformForSelectDisplayPeople)
}

async function loadSurveys(params) {
  const response = await searchSurveysAdmin(params)
  return response?.data?.map(transformForSelectDisplaySurvey)
}

async function loadItineraries(params) {
  const response = await searchItineraries(params)
  return response?.data?.map(transformForSelectDisplayItinerary)
}

// Search for customer users
async function loadCustomerUsers(params) {
  const { customerIds, nameTerm, pageSize = 50, pageOffset = 0, ...restOfParams } = params

  if (!customerIds?.length || !nameTerm) {
    return []
  }

  const response = await getUsers({
    customerIds,
    nameTerm,
    pageSize,
    pageOffset,
    ...restOfParams,
  })

  return response?.data?.map(user => ({
    value: user.id,
    label: `${user.firstName} ${user.lastName}`,
  }))
}

async function loadAdminUsers(params) {
  const { term } = params

  const response = await getUsers({
    nameTerm: term,
    activeUsersOnly: true,
    pageSize: 50,
    pageOffset: 0,
    role: ['admin', 'analyst'],
  })

  return response?.data?.map(user => ({
    id: user.id,
    value: user.id,
    label: `${user.firstName} ${user.lastName} (${user.email})`,
  }))
}

const debouncedLoadCustomers = debounce(loadCustomers, 250)
const debouncedLoadOrganizations = debounce(loadOrganizations, 250)
const debouncedLoadPeople = debounce(loadPeople, 250)
const debouncedLoadSurveys = debounce(loadSurveys, 250)
const debouncedLoadItineraries = debounce(loadItineraries, 250)
const debouncedLoadCustomerUsers = debounce(loadCustomerUsers, 250)
const debouncedLoadAdminUsers = debounce(loadAdminUsers, 250)

export {
  loadCustomers,
  debouncedLoadCustomers,
  debouncedLoadOrganizations,
  debouncedLoadPeople,
  debouncedLoadSurveys,
  debouncedLoadItineraries,
  loadCustomerUsers,
  debouncedLoadCustomerUsers,
  debouncedLoadAdminUsers,
}
